import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api/client.js';
import type { BlockedLearner, FollowGraphResponse, PublicLearner } from '../../lib/api/types.js';
import { useAsyncData } from '../../ui/components/LoadingState/useAsyncData.ts';
import { SegmentedNav } from '../../ui/components/Navigation.tsx';
import { Avatar } from '../../ui/primitives/Avatar.tsx';
import { Button } from '../../ui/primitives/Button.tsx';
import { Surface } from '../../ui/primitives/Surface.tsx';
import { Text } from '../../ui/primitives/Text.tsx';
import styles from './profile.module.css';

type Tab = 'following' | 'followers' | 'blocked';

const EMPTY: FollowGraphResponse = { following: [], followers: [] };

interface FollowListsProps {
  /**
   * Dev-only escape hatch: when the caller already has a graph and a block
   * list to show, no request is made and neither tab ever shows a loading
   * state. Used exactly once outside a test — `dev/profile-exploration.tsx`,
   * to compare the three Profile directions without a signed-in session.
   * Omit it and this fetches exactly as it always has.
   */
  fixture?: { graph: FollowGraphResponse; blocked: BlockedLearner[] };
}

/**
 * The two directions of the graph, and the people the learner has refused.
 *
 * BLOCKED IS A TAB, NOT A SETTING BURIED ELSEWHERE. A block is reversible and
 * the person who raised it is the only one who can see it, so the list has to
 * be somewhere they will find it again — next to the follows it severed, not
 * three screens away under an account menu.
 *
 * ONE REQUEST FOR BOTH FOLLOW DIRECTIONS, because the server returns both in
 * one response with the flags already filled. That is what lets "seguir de
 * volta" appear on a follower's row without this screen asking a second
 * question per person.
 */
export function FollowLists({ fixture }: FollowListsProps = {}) {
  const [tab, setTab] = useState<Tab>('following');

  const { data: graph, reload: reloadGraph } = useAsyncData<FollowGraphResponse>(
    () => (fixture ? Promise.resolve(fixture.graph) : api.follows()),
    EMPTY,
    [fixture],
  );
  const { data: blocked, reload: reloadBlocked } = useAsyncData<BlockedLearner[]>(
    () => (fixture ? Promise.resolve(fixture.blocked) : api.blocks()),
    [],
    [fixture],
  );

  return (
    <section className={styles.lists} aria-label="Quem você segue">
      <SegmentedNav
        label="Suas listas"
        items={[
          { id: 'following', label: `Seguindo (${graph.following.length})`, current: tab === 'following' },
          { id: 'followers', label: `Seguidores (${graph.followers.length})`, current: tab === 'followers' },
          ...(blocked.length > 0
            ? [{ id: 'blocked', label: `Bloqueados (${blocked.length})`, current: tab === 'blocked' }]
            : []),
        ]}
        onSelect={(id) => setTab(id as Tab)}
      />

      <Surface layer={1} radius="2xl" inset>
        {tab === 'blocked' ? (
          <BlockedList
            blocked={blocked}
            onChanged={() => {
              reloadBlocked();
              reloadGraph();
            }}
          />
        ) : (
          <PeopleList
            people={tab === 'following' ? graph.following : graph.followers}
            tab={tab}
            onChanged={() => {
              reloadGraph();
              reloadBlocked();
            }}
          />
        )}
      </Surface>
    </section>
  );
}

function PeopleList({
  people,
  tab,
  onChanged,
}: {
  people: PublicLearner[];
  tab: Tab;
  onChanged: () => void;
}) {
  if (people.length === 0) {
    return (
      <div className={styles.empty}>
        <Text as="p" variant="body">
          {tab === 'following'
            ? 'Você ainda não segue ninguém.'
            : 'Ninguém segue você ainda.'}
        </Text>
        <Text as="p" variant="body-sm" tone="muted">
          {tab === 'following'
            ? 'O ranking entre amigos começa quando você segue alguém que também toca.'
            : 'Compartilhe seu convite com quem toca com você.'}
        </Text>
      </div>
    );
  }

  return (
    <ul className={styles.people}>
      {people.map((person) => (
        <PersonRow key={person.id} person={person} onChanged={onChanged} />
      ))}
    </ul>
  );
}

/**
 * One person, with the one action that makes sense for them.
 *
 * A follower you do not follow back gets "Seguir de volta" — the single most
 * useful thing on this screen, and the reason both flags travel on every row.
 * Anyone you already follow gets "Deixar de seguir", which is destructive of
 * nothing and so is quiet rather than red.
 *
 * BLOCKING IS NOT OFFERED HERE. It lives on the person's own profile, behind
 * one more deliberate step, because it is the one action on this graph that
 * cannot be undone by simply repeating it: unblocking does not restore the
 * follows the block severed.
 */
function PersonRow({ person, onChanged }: { person: PublicLearner; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);

  async function act() {
    setBusy(true);
    try {
      await (person.following ? api.unfollow(person.id) : api.follow(person.id));
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className={styles.person}>
      <Link to={`/perfil/${person.id}`} className={styles.personLink}>
        <Avatar name={person.name} image={person.image} size="sm" />
        <Text as="span" variant="body">
          {person.name}
        </Text>
      </Link>

      {busy ? (
        <Button variant="ghost" size="compact" disabled disabledReason="Salvando">
          Salvando…
        </Button>
      ) : (
        <Button
          variant={person.following ? 'ghost' : 'secondary'}
          size="compact"
          onClick={() => void act()}
        >
          {person.following ? 'Deixar de seguir' : 'Seguir de volta'}
        </Button>
      )}
    </li>
  );
}

function BlockedList({
  blocked,
  onChanged,
}: {
  blocked: BlockedLearner[];
  onChanged: () => void;
}) {
  return (
    <ul className={styles.people}>
      {blocked.map((person) => (
        <li key={person.id} className={styles.person}>
          <span className={styles.personLink}>
            <Avatar name={person.name} image={person.image} size="sm" />
            <Text as="span" variant="body">
              {person.name}
            </Text>
          </span>

          <Button
            variant="ghost"
            size="compact"
            onClick={() => {
              void api.unblock(person.id).then(onChanged);
            }}
          >
            Desbloquear
          </Button>
        </li>
      ))}
    </ul>
  );
}
